// Code taken from @h2/common/components/user/edit
import styled from 'styled-components'
import { t } from 'ttag'
import { useMutation } from 'react-query'
import { useContext, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { api, errors } from '@h2/api'
import { Globals } from '@h2/globals'
import { useForm } from '@h2/common/components/hooks/useForm'
import { getFutureGraduationItems } from '@h2/common/utils/schools'
import {
  statesNameValueMap,
  statesAbbreviationValueMap,
  getStateFromAbbreviation
} from '@h2/common/utils/states'
import { US_STATES_LIST } from '@h2/common/constants'
import {
  primaryTypes,
  studentTypes,
  getItems,
  initItem
} from '@h2/common/components/user/utils'
import { setUser } from '@h2/common/actions/setUser'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { AutoSuggest } from '@h2/components/inputs/autoSuggest'
import { Select } from '@h2/components/inputs/select'
import { Flex } from '@h2/components/layout/flex'
import { Button } from '@h2/components/buttons/button'

const Actions = styled(Flex)`
  justify-content: space-between;

  > button {
    min-width: 100px;
  }
`
const Form = styled.form`
  > div:nth-child(n + 2) {
    margin-top: 15px;
  }

  > ${Actions}:last-child {
    margin-top: 30px;
  }
`
const HiddenRequiredField = styled(Field)`
  display: none;
`
const primaryTypeItems = getItems(primaryTypes)
const studentTypeItems = getItems(studentTypes)
const GraduationYear = ({ user, onError, onSuccess, children }) => {
  const {
    user: editor,
    seniorYear,
    defaultRegion,
    isNavianceElementary
  } = useContext(Globals)
  const dispatch = useDispatch()
  const isEditingSelf = useMemo(() => user.id === editor.id, [user, editor])
  const graduationYearItems = useMemo(
    () => getFutureGraduationItems(seniorYear, isNavianceElementary),
    [seniorYear, isNavianceElementary]
  )
  const {
    register,
    handleOnSubmit,
    setValidation,
    error,
    isInvalid,
    required,
    value: formValues
  } = useForm({
    validators: {
      graduationYear(form) {
        const entered = formValues.graduationYear
        const parsed = form.graduationYear

        if (entered) {
          const maxYear = new Date().getFullYear() + 24

          if (isNaN(parsed) || parsed.toString().length !== 4) {
            return t`Graduation Year must be a four digit number`
          }

          if (parsed < 1920 || parsed > maxYear) {
            return t`Graduation Year must be between 1920 and ${maxYear}`
          }
        }
      }
    },
    initialValues: {
      email: user.email,
      state: getStateFromAbbreviation(user.state ?? defaultRegion),
      primaryType: initItem(user.primaryType, primaryTypeItems),
      studentType: initItem(user.studentType, studentTypeItems)
    }
  })
  const { mutate, isLoading } = useMutation(
    body => {
      return api.fetch(`/personalProfile/${isEditingSelf ? 'me' : user.id}`, {
        method: 'POST',
        body: JSON.stringify(body)
      })
    },
    {
      onSuccess: useCallback(
        updatedUser => {
          if (isEditingSelf) {
            dispatch(setUser(updatedUser))
          }

          if (typeof onSuccess === 'function') {
            onSuccess(updatedUser)
          }
        },
        [dispatch, isEditingSelf, onSuccess]
      ),
      onError: useCallback(
        err => {
          const validation = errors.getValidation(err)

          if (validation) {
            setValidation(validation)
          } else if (typeof onError === 'function') {
            onError(err)
          }
        },
        [setValidation, onError]
      )
    }
  )
  const onSubmit = useCallback(
    form => {
      const obsolete = Object.keys(required).filter(field => !required[field])

      Object.keys(form).forEach(field => {
        if (obsolete.includes(field) || Number.isNaN(form[field]) || !form[field]) {
          delete form[field]
        }
      })

      if (isEditingSelf) {
        delete form.permission
      }

      if (user.isRoot) {
        delete form.permission
        delete form.eid
      }

      mutate(form)
    },
    [required, mutate, isEditingSelf, user.isRoot]
  )

  return (
    <Form onSubmit={handleOnSubmit(onSubmit)} noValidate>
      <Field
        id="graduation-field"
        htmlFor="graduation-select"
        desc={error.graduationYear}
        status={error.graduationYear ? 'error' : 'default'}
        label={t`High School Graduation Year`}>
        <AutoSuggest
          id="graduation-select"
          labelledBy="graduation-field"
          placeholder={t`Start typing for suggestions...`}
          items={graduationYearItems}
          {...register('graduationYear', {
            parser: val => parseInt(val?.value ?? val, 10)
          })}
        />
      </Field>
      {/* The following required fields are required by the API, but since these have already been entered by the user before, we hide it now */}
      <HiddenRequiredField
        label={t`Email`}
        desc={t`Validated emails cannot be changed`}
        status={error.email ? 'error' : 'default'}>
        <TextInput {...register('email')} />
      </HiddenRequiredField>
      <HiddenRequiredField
        id="states-field"
        htmlFor="states-select"
        label={t`State`}
        desc={error.state}
        status={error.state ? 'error' : 'default'}>
        <AutoSuggest
          id="states-select"
          labelledBy="states-field"
          items={US_STATES_LIST}
          placeholder={t`Enter your state...`}
          {...register('state', {
            parser: val =>
              val?.value ??
              statesNameValueMap.get(val?.trim().toLowerCase()) ??
              statesAbbreviationValueMap.get(val?.trim().toLowerCase())
          })}
        />
      </HiddenRequiredField>
      <HiddenRequiredField
        id="primary-type-field"
        htmlFor="primary-type-select"
        desc={error.primaryType}
        status={error.primaryType ? 'error' : 'default'}
        label={t`User Type`}>
        <Select
          id="primary-type-select"
          labelledBy="primary-type-field"
          items={primaryTypeItems}
          {...register('primaryType', { parser: val => val?.value })}
        />
      </HiddenRequiredField>
      <HiddenRequiredField
        id="student-type-field"
        htmlFor="student-type-select"
        desc={error.studentType}
        status={error.studentType ? 'error' : 'default'}
        label={t`Student Type`}>
        <Select
          id="student-type-select"
          labelledBy="student-type-field"
          items={studentTypeItems}
          {...register('studentType', {
            parser: val => val?.value
          })}
        />
      </HiddenRequiredField>
      <Actions>
        {children}
        <Button
          type="submit"
          size="large"
          isLoading={isLoading}
          isDisabled={isInvalid || isLoading}>
          {t`Submit`}
        </Button>
      </Actions>
    </Form>
  )
}

export { GraduationYear }
